/**
 * IndexEntriesList - Container for displaying and managing index channel entries
 */

import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  CheckCircle as ImportIcon,
  Cancel as SkipAllIcon,
} from '@mui/icons-material';
import IndexEntryCard from './IndexEntryCard';
import { ParsedIndexEntry, LibraryMatchResult, LibraryDecision } from '@/types/indexChannel';

interface Library {
  id: number;
  canonical_name: string;
}

interface IndexEntriesListProps {
  entries: ParsedIndexEntry[];
  libraryMatches: LibraryMatchResult[];
  libraries: Library[];
  selectedEntries: Set<string>;
  decisions: Map<string, LibraryDecision>;
  onToggleEntry: (entryId: string) => void;
  onUpdateRange: (entryId: string, start: number, end: number) => void;
  onDecisionChange: (entryId: string, decision: LibraryDecision) => void;
  onImport: () => void;
  onSkipAll: () => void;
  onViewMessage: (messageId: number, chatId: number) => void;
  isImporting: boolean;
  stats?: {
    total_entries: number;
    matched: number;
    unmatched: number;
    total_manuscripts: number;
  };
}

const IndexEntriesList: React.FC<IndexEntriesListProps> = ({
  entries,
  libraryMatches,
  libraries,
  selectedEntries,
  decisions,
  onToggleEntry,
  onUpdateRange,
  onDecisionChange,
  onImport,
  onSkipAll,
  onViewMessage,
  isImporting,
  stats,
}) => {
  if (entries.length === 0) {
    return null;
  }

  const getMatchForEntry = (entry: ParsedIndexEntry): LibraryMatchResult | undefined => {
    return libraryMatches.find((match) => match.hashtag === entry.library_hashtag);
  };

  const selectedCount = selectedEntries.size;
  const hasSelection = selectedCount > 0;

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Index Channel Entries
        </Typography>

        {stats && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Found {stats.total_entries} {stats.total_entries === 1 ? 'entry' : 'entries'} in index channel
              <br />
              {stats.matched} matched to existing libraries, {stats.unmatched} need decisions
              <br />
              Total manuscripts: {stats.total_manuscripts}
            </Alert>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onImport}
            disabled={!hasSelection || isImporting}
            startIcon={isImporting ? <CircularProgress size={20} /> : <ImportIcon />}
          >
            {isImporting
              ? 'Importing...'
              : `Import ${selectedCount} Selected ${selectedCount === 1 ? 'Entry' : 'Entries'}`}
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            onClick={onSkipAll}
            disabled={isImporting}
            startIcon={<SkipAllIcon />}
          >
            Skip All
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />
      </Box>

      <Grid container spacing={2}>
        {entries.map((entry) => {
          const entryId = `${entry.index_no}-${entry.library_hashtag}`;
          const match = getMatchForEntry(entry);
          const decision = decisions.get(entryId);

          return (
            <Grid item xs={12} key={entryId}>
              <IndexEntryCard
                entry={entry}
                libraryMatch={match}
                libraries={libraries}
                selected={selectedEntries.has(entryId)}
                decision={decision}
                onToggleSelect={onToggleEntry}
                onUpdateRange={onUpdateRange}
                onDecisionChange={onDecisionChange}
                onViewMessage={onViewMessage}
              />
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onImport}
          disabled={!hasSelection || isImporting}
          startIcon={isImporting ? <CircularProgress size={20} /> : <ImportIcon />}
        >
          {isImporting
            ? 'Importing...'
            : `Import ${selectedCount} Selected ${selectedCount === 1 ? 'Entry' : 'Entries'}`}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={onSkipAll}
          disabled={isImporting}
          startIcon={<SkipAllIcon />}
        >
          Skip All
        </Button>
      </Box>
    </Paper>
  );
};

export default IndexEntriesList;
